
import { Grid } from '@mui/material';
import { useCallback, useState, useMemo } from 'react';
import { useLanguageContext } from '../../contexts/LanguageContext';
import { usePartnerPmsContext } from '../../contexts/PartnerPmsContext';
import { ActionButton } from '../Button';
import SelectField from '../SelectField';
import { GovernanceStatusData, GovernanceStatusEnum, OccupancyStatusData, OccupancyStatusEnum, RoomStatusData, RoomStatusEnum } from '../../data/Enums';
import Drawer, { IDefaultDrawerProps } from '../Drawer';
import Switch from '../Switch';
import { useUserContext } from '../../contexts/UserContext';
import { IBlock, IFloor, IRoomType } from '../../interfaces/IPartnerPms';


interface IFilter {
    occupancyStatus: OccupancyStatusEnum[];
    governanceStatus: GovernanceStatusEnum[];
    roomStatus: RoomStatusEnum[];
    block: IBlock[];
    floor: IFloor[];
    roomType: IRoomType[];
    onlyUserRooms: boolean;
}


const Filters: React.FC<IDefaultDrawerProps> = ({
    open,
    handleClose
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useUserContext();
    const { 
        selectedOccupancyStatus,
        selectedGovernanceStatus,
        selectedRoomStatus,
        selectedBlock,
        selectedFloor,
        selectedRoomType, 
        hasUserRooms, 
        onlyUserRooms, 
        handleChangeSelectedFilters 
    } = usePartnerPmsContext();


    const [filters, setFilters] = useState<IFilter>({
        onlyUserRooms: onlyUserRooms,
        occupancyStatus: selectedOccupancyStatus,
        governanceStatus: selectedGovernanceStatus,
        roomStatus: selectedRoomStatus,
        block: selectedBlock,
        floor: selectedFloor,
        roomType: selectedRoomType
    });


    const enterprisePartnerPms = useMemo(() => {
        return {
            blocks: enterprise?.partnerPms.blocks ?? [],
            floors: enterprise?.partnerPms.floors ?? [],
            roomTypes: enterprise?.partnerPms.roomTypes ?? [],
            hasUserRooms: (enterprise?.partnerPms.userRooms ?? []).length > 0
        }
    }, [enterprise]);


    const _handleApplyFilters = useCallback((filters: IFilter) => {
        handleChangeSelectedFilters( 
            filters.occupancyStatus, 
            filters.governanceStatus, 
            filters.roomStatus,
            filters.block, 
            filters.floor, 
            filters.roomType,
            filters.onlyUserRooms,
        );
        handleClose();

        // eslint-disable-next-line
    }, []);


    const handleChangeFilterValue = useCallback((name: string, value: any) => {
        setFilters({
            ...filters,
            [name]: value
        });

        // eslint-disable-next-line
    }, [filters]);


    const footer = useMemo(() => {
        return <ActionButton
            action='apply'
            fullWidth={true}
            className='text-alwaysshow'
            onClick={() => _handleApplyFilters(filters)}
        />;
        // eslint-disable-next-line
    }, [filters, translate]);
    

    return (
        <Drawer
            anchor='right'
            open={open}
            handleClose={handleClose}
            title={translate('filter')}
            footer={footer}
        >
            <Grid container>
                <Grid item xs={12}>
                    <SelectField
                        label={translate('occupancyStatus')}
                        name='occupancyStatus'
                        value={filters.occupancyStatus.length > 0 ? filters.occupancyStatus : [0]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const occupancyStatus = filters.occupancyStatus.length > 0 && values.includes(0) ? [] : Object.values(OccupancyStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            handleChangeFilterValue(name, occupancyStatus);
                        }}
                        multiple={true}
                        data={[{ value: 0, text: translate('all') }].concat(Object.values(OccupancyStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('governanceStatus')}
                        name='governanceStatus'
                        value={filters.governanceStatus.length > 0 ? filters.governanceStatus : [0]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const governanceStatus = filters.governanceStatus.length > 0 && values.includes(0) ? [] : Object.values(GovernanceStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            handleChangeFilterValue(name, governanceStatus);
                        }}
                        multiple={true}
                        data={[{ value: 0, text: translate('all') }].concat(Object.values(GovernanceStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('roomStatus')}
                        name='roomStatus'
                        value={filters.roomStatus.length > 0 ? filters.roomStatus : [0]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const roomStatus = filters.roomStatus.length > 0 && values.includes(0) ? [] : Object.values(RoomStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            handleChangeFilterValue(name, roomStatus);
                        }}
                        multiple={true}
                        data={[{ value: 0, text: translate('all') }].concat(Object.values(RoomStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('block')}
                        name='block'
                        value={filters.block.length > 0 ? filters.block.map(b => +b.id) : [0]}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const block = filters.block.length > 0 && values.includes(0) ? [] : enterprisePartnerPms.blocks.filter(b => values.includes(+b.id));
                            handleChangeFilterValue(name, block);
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(enterprisePartnerPms.blocks.map(b => {
                            return {
                                value: +b.id,
                                text: b.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('floor')}
                        name='floor'
                        value={filters.floor.length > 0 ? filters.floor.map(f => +f.id) : [0]}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const floor = filters.floor.length > 0 && values.includes(0) ? [] : enterprisePartnerPms.floors.filter(f => values.includes(+f.id));
                            handleChangeFilterValue(name, floor);
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(enterprisePartnerPms.floors.map(f => {
                            return {
                                value: +f.id,
                                text: f.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('roomType')}
                        name='roomType'
                        value={filters.roomType.length > 0 ? filters.roomType.map(r => +r.id) : [0]}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const roomType = filters.roomType.length > 0 && values.includes(0) ? [] : enterprisePartnerPms.roomTypes.filter(r => values.includes(+r.id));
                            handleChangeFilterValue(name, roomType);
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(enterprisePartnerPms.roomTypes.map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>

                {
                    hasUserRooms && <Grid item xs={12} marginTop={2} className='textalign-center'>
                        <Switch
                            label={translate('_showOnlyRoomsAssignedToMe')}
                            labelPlacement='bottom'
                            name='onlyUserRooms'
                            value={filters.onlyUserRooms ? 0 : 1}
                            checked={filters.onlyUserRooms}
                            onChange={e => handleChangeFilterValue(e.target.name, Boolean(+e.target.value))}
                        />
                    </Grid>
                }
            </Grid>
        </Drawer>
    );
}

export default Filters;